import React, { useEffect, useState } from 'react';
import { useSector } from '../../contexts/SectorContext';
import { getMemory, refreshMemory, createApproval, MemoryDto } from '../services/MendsVisionService';
import { CommercialFieldList } from './CommercialFieldList';
import { describeCreateOutcome } from '../lib/createApprovalOutcome';
import { RegisterHistoryList } from './RegisterHistoryList';
import { DocumentConditionsList } from './DocumentConditionsList';
import '../styles/mendsAi.css';

interface Props {
  contactId: number;
  contactName?: string;
}

export const ContactMemoryPanel: React.FC<Props> = ({ contactId, contactName }) => {
  const { selectedSectorId } = useSector();
  const [data, setData] = useState<MemoryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  /** Relatos do Registrar só carregam ao abrir a seção (uma requisição a menos por conversa aberta). */
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!selectedSectorId || !contactId) return;
    const controller = new AbortController();
    setQueued(false);
    setError(null);
    setLoading(true);
    getMemory(Number(selectedSectorId), contactId, controller.signal)
      .then((d) => { if (!controller.signal.aborted) setData(d); })
      .catch((e) => { if (!controller.signal.aborted) setError(e?.message || 'Erro'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); };
  }, [selectedSectorId, contactId]);

  const onRefresh = async () => {
    if (!selectedSectorId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await refreshMemory(Number(selectedSectorId), contactId);
      setData(d);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Falha ao gerar resumo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mai-memory-panel mai-card">
      <div className="mai-card-row">
        <div>
          <div className="mai-eyebrow">Resumo vivo</div>
          <p className="mai-card-title" style={{ marginBottom: 0 }}>
            {contactName || 'Cliente'}
          </p>
        </div>
        <button className="mai-btn mai-btn-soft" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : 'Atualizar'}
        </button>
      </div>
      {error && <p style={{ color: '#fca5a5', fontSize: 13 }}>{error}</p>}
      <p className="mai-card-detail" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>
        {data?.summary || (loading ? 'Carregando memória…' : 'Sem memória ainda — clique em Atualizar (1 chamada IA).')}
      </p>
      <CommercialFieldList properties={data?.properties} history={data?.history} />
      {(!data?.properties?.length) && data?.facts && data.facts.length > 0 && (
        <div className="mai-facts">
          {data.facts.slice(0, 6).map((f, i) => (
            <span key={i} className="mai-fact">{f}</span>
          ))}
        </div>
      )}
      {data?.suggestion && (
        <div className="mai-suggestion">
          <div className="mai-section-label" style={{ marginBottom: 6 }}>Sugestão da IA</div>
          <p className="mai-card-detail" style={{ margin: 0 }}>{data.suggestion}</p>
          {selectedSectorId && (
            <button
              className="mai-btn"
              style={{ marginTop: 10 }}
              onClick={async () => {
                const created = await createApproval({
                  sectorId: Number(selectedSectorId),
                  contactId,
                  contactName,
                  title: `Fazer isso — ${contactName || 'cliente'}`,
                  body: data.suggestion || '',
                  suggestedAction: data.suggestion || '',
                  actionKind: 'send_message',
                  actionPayload: JSON.stringify({ text: data.suggestion, contactId }),
                });
                const outcome = describeCreateOutcome(created);
                if (outcome.kind !== 'queued') {
                  setError(outcome.message);
                  return;
                }
                setError(null);
                setQueued(true);
              }}
              disabled={queued}
            >
              {queued ? 'Na fila de aprovação' : 'Fazer isso'}
            </button>
          )}
        </div>
      )}
      {selectedSectorId && (
        <DocumentConditionsList sectorId={Number(selectedSectorId)} contactId={contactId} />
      )}
      {selectedSectorId && (
        <details
          className="mai-reg-contact-history"
          style={{ marginTop: 12 }}
          onToggle={(e) => setHistoryOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary className="mai-section-label">Relatos registrados</summary>
          {historyOpen && <RegisterHistoryList
            sectorId={Number(selectedSectorId)}
            contactId={contactId}
            limit={10}
            compact
            emptyText="Nenhum relato registrado para este cliente."
          />}
        </details>
      )}
    </div>
  );
};
